import styled from "@emotion/styled";
import type { ConcentratedLiquidityPosition } from "@stepfinance/liquidity";
import {
  ExtraBoldText,
  formatBalance,
  greenTag,
  PLUS_JAKARTA_SANS,
  redTag,
  StepAssetBreakdownPopoverChip,
  StepPopoverCellWrapper,
  TagInfo,
} from "@stepfinance/react";
import type { FC } from "react";
import { useState } from "react";

import switchArrows from "../../../assets/switch-arrows.svg";
import { useLanguageContext } from "../../../contexts/language/language";
import { NumberLabel, StepPopoverAssetWithIconRow } from "../../shared";
import CellValue from "../../Table/CellValue";

type ConcentratedLiquidityBreakdownProps = ConcentratedLiquidityPosition & {
  price: number | null;
  isAboveRange: boolean;
  isBelowRange: boolean;
};

export const ConcentratedLiquidityBreakdown: FC<ConcentratedLiquidityBreakdownProps> = ({
  liquidity,
  price,
  valueInUSD,
  isAboveRange,
  isBelowRange,
}) => {
  const [minMaxIndex, setMinMaxIndex] = useState(1);

  const onClick = () => {
    setMinMaxIndex(minMaxIndex === 0 ? 1 : 0);
  };

  const {
    currentLanguageData: {
      components: {
        table: { assetBreakdownPopover },
      },
      pages: {
        dashboard: {
          modules: {
            liquidity: { concentratedLiquidity: CLMMText },
          },
        },
      },
    },
  } = useLanguageContext();

  const liquidityComponents = liquidity.map((liquidityHalf, i) => {
    const amount = formatBalance(liquidityHalf.amount, liquidityHalf.token?.decimals);
    const symbol = liquidityHalf.token?.symbol || "";
    const mintAddress = liquidityHalf.token?.address;
    const minMaxInfo = liquidity[minMaxIndex];
    const minMaxSymbol = minMaxInfo.token?.symbol;

    // checking for index of 1 to position the min max display
    return i === 1 ? (
      <div key={liquidityHalf.token?.address}>
        <MinMaxRow onClick={onClick}>
          <ExtraBoldText>{CLMMText.minimum}</ExtraBoldText>
          <NumberLabel>{minMaxInfo.minPrice?.toFixed(6)}</NumberLabel>
          <ExtraBoldText>{minMaxSymbol}</ExtraBoldText>
          <img src={switchArrows} alt="" />
        </MinMaxRow>
        <StepPopoverAssetWithIconRow
          key={liquidityHalf.token?.address}
          tokenIconMintAddress={mintAddress}
          amount={amount}
          symbol={symbol}
          className="space-between"
        />
        <MinMaxRow onClick={onClick}>
          <ExtraBoldText>{CLMMText.maximum}</ExtraBoldText>
          <NumberLabel>{minMaxInfo.maxPrice?.toFixed(6)}</NumberLabel>
          <ExtraBoldText>{minMaxSymbol}</ExtraBoldText>
          <img src={switchArrows} alt="" />
        </MinMaxRow>
      </div>
    ) : (
      <StepPopoverAssetWithIconRow
        key={liquidityHalf.token?.address}
        tokenIconMintAddress={mintAddress}
        amount={amount}
        symbol={symbol}
        className="space-between"
      />
    );
  });

  const getTitleTag = () => {
    let text;
    let tagColor;
    if (!isAboveRange && !isBelowRange) {
      text = CLMMText.inRange;
      tagColor = greenTag;
    } else {
      text = CLMMText.outofRange;
      tagColor = redTag;
    }

    return (
      <TagInfo tagType={"text"} tagColorOverride={tagColor}>
        <TagText>{text}</TagText>
      </TagInfo>
    );
  };

  const title = (
    <Title>
      <ExtraBoldText>{assetBreakdownPopover.title}</ExtraBoldText>
      {getTitleTag()}
    </Title>
  );

  return (
    <StepPopoverCellWrapper>
      <StepAssetBreakdownPopoverChip
        title={title}
        content={liquidityComponents}
        assetValue={valueInUSD ?? 0}
        assetValueDisplay={
          <CellValue cellType="userPositionValue" fontWeight="bold">
            {price}
          </CellValue>
        }
      />
    </StepPopoverCellWrapper>
  );
};

const MinMaxRow = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: flex-start;
  margin: 0.5em 0 0.5em -5px;
  & > * {
    margin-right: 0.5em;
  }
`;

const TagText = styled.span`
  text-transform: none;
  font-family: ${PLUS_JAKARTA_SANS}, sans-serif;
`;

const Title = styled.span`
  display: flex;
  justify-content: space-between;
  width: 245px;
`;
